import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { attributionFond, modeleTuiles, sousDomaines } from '../utils/fondCarte';
import { contenuBulleCollecte } from '../utils/bulleCollecte';
import { locationApi } from '../api/axios';
import { Badge, Chargement, IconeTelecharger, LienRetour, Message, TitreSection } from '../components/charte/Elements';
import { OngletsLiens, panneauOnglet } from '../components/charte/Onglets';
import BoutonAide from '../components/charte/BoutonAide';
import ResultatsMission from '../components/fiche/ResultatsMission';
import DocumentsMission from '../components/fiche/DocumentsMission';
import { AIDE } from '../aide/textesAide';
import { CHARTE, COULEURS } from '../theme/charte';
import { styleStatutMission } from '../utils/statutsMission';
import {
  avancement, ChampMission, idMission, Mission, SyntheseSpotters,
} from '../donnees/missions';
import { categorieMission, joursDepuis, jourCourt, situationMission } from '../donnees/situation';
import { filMission } from '../donnees/filMission';
import {
  chargerCollectes, chargerStats, Collecte, LIBELLE_STATUT_COLLECTE, StatsCollectes, telechargerExport,
} from '../donnees/collectes';
import {
  chargerDocuments, documentsDeLaMission, DocumentClient, enregistrerFichier,
  messageErreurDocuments, messageErreurPdf, telechargerPdf,
} from '../donnees/documents';
import { marquerResultatsVus } from '../donnees/relecture';

/* ── Libellés ── */
const FIELD_TYPE_LABELS: Record<string, string> = {
  texte: 'Texte', nombre: 'Nombre', photo: 'Photo',
  selection: 'Sélection', date: 'Date', booleen: 'Oui / non', gps: 'Position',
};

const fmtDate = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};
const fmtHeure = (d: Date) => d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

const COULEUR_COLLECTE: Record<string, string> = {
  valide: COULEURS.succes,
  en_attente: CHARTE.ocreClair,
  rejete: COULEURS.erreur,
};

const CLES_ONGLETS = ['suivi', 'resultats', 'documents'];
const RAFRAICHISSEMENT_MS = 30_000;

const libelleChamp = (c: ChampMission) => c.label || c.nom || '';

/* ═══════════════════════════════════════════════════════ */

const MissionDetail: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const demande = params.get('onglet') ?? 'suivi';
  const onglet = CLES_ONGLETS.includes(demande) ? demande : 'suivi';

  const [mission, setMission] = useState<Mission | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreurMission, setErreurMission] = useState('');

  const [collectes, setCollectes] = useState<Collecte[]>([]);
  const [stats, setStats] = useState<StatsCollectes | null>(null);
  const [collectesChargees, setCollectesChargees] = useState(false);
  const [majLe, setMajLe] = useState<Date | null>(null);
  const [echecDepuis, setEchecDepuis] = useState<Date | null>(null);

  const [cycles, setCycles] = useState<Mission[]>([]);

  const [documents, setDocuments] = useState<DocumentClient[] | null>(null);
  const [erreurDocuments, setErreurDocuments] = useState('');
  const [pdfEnCours, setPdfEnCours] = useState<string | null>(null);
  const [erreurAction, setErreurAction] = useState('');

  const [exportEnCours, setExportEnCours] = useState(false);

  const mapRef = useRef<HTMLDivElement>(null);
  const carteRef = useRef<L.Map | null>(null);
  const coucheRef = useRef<L.LayerGroup | null>(null);
  const [cartePrete, setCartePrete] = useState(false);

  /* ── Mission ── */
  useEffect(() => {
    let actif = true;
    setChargement(true);
    locationApi.get(`/quest/${id}`)
      .then(res => { if (actif) setMission(res.data); })
      .catch(err => {
        if (!actif) return;
        const s = err?.response?.status;
        setErreurMission(s === 404 || s === 403
          ? "Cette mission est introuvable, ou elle n'est pas rattachée à votre compte."
          : !err?.response
            ? 'Impossible de joindre Tribe. Vérifiez votre connexion puis rechargez la page.'
            : "La mission n'a pas pu être chargée. Réessayez dans quelques minutes.");
      })
      .finally(() => { if (actif) setChargement(false); });
    return () => { actif = false; };
  }, [id]);

  useEffect(() => {
    if (mission?.titre) document.title = `${mission.titre} — Tribe`;
    return () => { document.title = 'Tribe — Portail client'; };
  }, [mission?.titre]);

  /* ── Cycles (missions récurrentes) ── */
  useEffect(() => {
    if (!mission?.cycleActif) return;
    locationApi.get(`/quest/${id}/cycles`).then(r => setCycles(Array.isArray(r.data) ? r.data : [])).catch(() => setCycles([]));
  }, [mission?.cycleActif, id]);

  /* ── Collectes : chargées puis rafraîchies toutes les 30 s ──
     Un échec n'efface pas les données déjà affichées : il est signalé, avec
     l'heure de la dernière mise à jour réussie. */
  const rafraichir = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([chargerCollectes(id), chargerStats(id).catch(() => null)]);
      setCollectes(c);
      if (s) setStats(s);
      setMajLe(new Date());
      setEchecDepuis(null);
    } catch {
      setEchecDepuis(prev => prev ?? new Date());
    } finally {
      setCollectesChargees(true);
    }
  }, [id]);

  useEffect(() => {
    if (!mission) return;
    rafraichir();
    const iv = setInterval(rafraichir, RAFRAICHISSEMENT_MS);
    return () => clearInterval(iv);
  }, [mission, rafraichir]);

  /* ── Documents de la mission : chargés avec elle (en-tête, fil, onglet) ── */
  useEffect(() => {
    if (!mission || documents !== null) return;
    chargerDocuments()
      .then(d => { setDocuments(documentsDeLaMission(d, id)); setErreurDocuments(''); })
      .catch(err => { setDocuments([]); setErreurDocuments(messageErreurDocuments(err)); });
  }, [documents, mission, id]);

  /* ── Résultats ouverts : la livraison n'est plus « à relire » ── */
  useEffect(() => {
    if (onglet === 'resultats' && collectesChargees) marquerResultatsVus(id);
  }, [onglet, collectesChargees, id]);

  /* ── Carte : initialisée une fois, sur le fond clair CARTO Voyager ── */
  useEffect(() => {
    if (!mission || !mapRef.current || carteRef.current) return;
    let bornes: L.LatLngBoundsExpression | null = null;
    if (mission.latMin != null && mission.latMax != null && mission.lngMin != null && mission.lngMax != null) {
      bornes = [[mission.latMin, mission.lngMin], [mission.latMax, mission.lngMax]];
    } else if (mission.zone?.coordinates && mission.zone.coordinates.length >= 2) {
      const c = mission.zone.coordinates;
      bornes = [[c[0][0], c[0][1]], [c[1][0], c[1][1]]];
    }
    const carte = L.map(mapRef.current, {
      center: [14.6937, -17.4441],
      zoom: 9,
      scrollWheelZoom: false,
      zoomControl: false,
      // Sur téléphone, un doigt fait défiler la page au lieu de déplacer la carte (zoom au pincement).
      dragging: !L.Browser.mobile,
    });
    // Boutons + et − en haut à droite, comme sur la maquette.
    L.control.zoom({ position: 'topright', zoomInTitle: 'Agrandir', zoomOutTitle: 'Réduire' }).addTo(carte);
    L.tileLayer(modeleTuiles(), { subdomains: sousDomaines(), attribution: attributionFond, maxZoom: 19 }).addTo(carte);
    if (bornes) {
      L.rectangle(bornes, { color: COULEURS.texte, weight: 1.5, dashArray: '6 5', fill: false }).addTo(carte);
      carte.fitBounds(bornes, { padding: [16, 16] });
    }
    carteRef.current = carte;
    coucheRef.current = L.layerGroup().addTo(carte);
    setCartePrete(true);
    return () => {
      carte.remove();
      carteRef.current = null;
      coucheRef.current = null;
      setCartePrete(false);
    };
  }, [mission]);

  // Panneau masqué puis réaffiché : Leaflet doit recalculer la taille de la carte.
  useEffect(() => {
    if (onglet === 'suivi') setTimeout(() => carteRef.current?.invalidateSize(), 0);
  }, [onglet]);

  useEffect(() => {
    const couche = coucheRef.current;
    if (!cartePrete || !couche) return;
    couche.clearLayers();
    collectes.forEach(c => {
      const lat = parseFloat(String(c.latitude));
      const lng = parseFloat(String(c.longitude));
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return;
      L.circleMarker([lat, lng], {
        radius: 5, color: COULEURS.blanc, weight: 1,
        fillColor: COULEUR_COLLECTE[c.statut ?? ''] ?? COULEURS.neutre, fillOpacity: 0.95,
      })
        // Contenu construit en éléments DOM et en texte : jamais de HTML venu du serveur.
        .bindPopup(() => contenuBulleCollecte(c, fmtDate), { maxWidth: 280, className: 'bulle-carte' })
        .addTo(couche);
    });
  }, [collectes, cartePrete]);

  /* ── Actions ── */
  const exporter = async () => {
    setExportEnCours(true);
    setErreurAction('');
    try {
      const { blob, nom } = await telechargerExport(id);
      enregistrerFichier(blob, nom);
    } catch (err: any) {
      setErreurAction(err?.response?.status === 403
        ? "Votre compte n'a pas accès à l'export de cette mission. Écrivez à missions@tribe.sn."
        : !err?.response
          ? 'Impossible de joindre Tribe. Vérifiez votre connexion puis réessayez.'
          : "L'export n'a pas pu être préparé. Réessayez dans quelques minutes.");
    } finally {
      setExportEnCours(false);
    }
  };

  const pdf = async (doc: DocumentClient) => {
    setPdfEnCours(String(doc.id));
    setErreurAction('');
    try { await telechargerPdf(doc); } catch (err) { setErreurAction(messageErreurPdf(doc, err)); } finally { setPdfEnCours(null); }
  };

  /* ── Rendu ── */
  if (chargement) return <div className="page"><Chargement /></div>;

  if (erreurMission || !mission) return (
    <div className="page">
      <div className="bloc pile" style={{ alignItems: 'flex-start' }}>
        <Message genre="erreur">{erreurMission || 'Mission introuvable.'}</Message>
        <Link to="/missions" className="bouton bouton--secondaire">Retour aux missions</Link>
      </div>
    </div>
  );

  const champs = mission.champsCollecte || mission.champs || [];
  const spotters: SyntheseSpotters | null | undefined = stats?.spotters ?? mission.spotters;
  const nb = (statut: string) => collectes.filter(c => c.statut === statut).length;
  const docs = documents ?? [];
  const situation = situationMission(mission, docs);
  const st = situation.statut;

  /* Chiffres clés (maquette) : tout se calcule à partir des collectes lues. */
  const validees = stats?.valides ?? nb('valide');
  const enVerification = stats?.enAttente ?? nb('en_attente');
  const rejetees = stats?.rejetees ?? nb('rejete');
  const attendues = mission.collectesAttendues ?? 0;
  const pctValidees = attendues > 0 ? Math.min(100, Math.round((validees / attendues) * 100)) : avancement(mission);
  const ilYa24h = Date.now() - 86_400_000;
  const depuisHier = collectes.filter(c => c.statut === 'valide' && c.createdAt && new Date(c.createdAt).getTime() >= ilYa24h).length;
  const joursCollecte = Math.max(1, joursDepuis(mission.dateDebut) ?? 0);
  const rythme = spotters && spotters.mobilises > 0 && mission.dateDebut
    ? (validees / spotters.mobilises / joursCollecte).toLocaleString('fr-FR', { maximumFractionDigits: 1 }) : null;
  const decidees = validees + rejetees;
  const qualite = decidees > 0 ? Math.round((validees / decidees) * 100) : null;
  const ages = collectes.filter(c => c.statut === 'valide').map(c => joursDepuis(c.createdAt)).filter((j): j is number => j != null);
  const fraicheur = ages.length > 0 ? Math.round(ages.reduce((t, j) => t + j, 0) / ages.length) : null;

  const meta = [
    categorieMission(mission),
    mission.dateDebut && situation.categorie !== 'etude' && situation.categorie !== 'decision' ? `depuis le ${jourCourt(mission.dateDebut)}` : null,
    situation.livraison !== '—' ? `${mission.statut === 'terminee' ? 'livrée le' : 'livraison'} ${situation.livraison}` : null,
  ].filter(Boolean).join(' · ');

  const fil = filMission(mission, docs, spotters?.mobilises);

  /* Action principale de l'en-tête : ce que le client a à faire. */
  const actionPrincipale = situation.devisASigner ? (
    <Link to={`/documents#devis-${situation.devisASigner.id}`} className="bouton">Signer le devis</Link>
  ) : situation.factureARegler ? (
    <Link to={{ search: '?onglet=documents' }} replace className="bouton">
      Régler {situation.factureARegler.natureFacture === 'ACOMPTE' ? "l'acompte" : 'la facture'}
    </Link>
  ) : mission.statut === 'terminee' ? (
    <button type="button" className="bouton" onClick={exporter} disabled={exportEnCours}>
      {exportEnCours ? 'Préparation…' : 'Exporter les résultats'}
    </button>
  ) : null;
  const sujetAjustement = encodeURIComponent(`Ajustement de la mission « ${mission.titre} »`);

  const alerteRafraichissement = echecDepuis && (
    <Message genre="attention">
      Les collectes n'ont pas pu être mises à jour
      {majLe ? ` depuis ${fmtHeure(majLe)}` : ''}. Les informations affichées peuvent être en retard.
      Nouvel essai automatique toutes les 30 secondes.{' '}
      <button type="button" className="bouton bouton--discret bouton--petit" onClick={rafraichir}>Réessayer maintenant</button>
    </Message>
  );

  const onglets = [
    { cle: 'suivi', libelle: 'Suivi' },
    { cle: 'resultats', libelle: collectesChargees ? `Résultats · ${collectes.length.toLocaleString('fr-FR')}` : 'Résultats' },
    { cle: 'documents', libelle: documents ? `Documents · ${documents.length}` : 'Documents' },
  ];

  return (
    <div className="page" style={{ gap: '26px' }}>
      <LienRetour vers="/missions">Missions</LienRetour>

      <header className="entete-page" style={{ alignItems: 'flex-start', gap: '20px' }}>
        <div className="entete-page__textes" style={{ gap: '10px' }}>
          <div className="ligne" style={{ gap: '12px' }}>
            <Badge libelle={st.libelle} couleur={st.couleur} />
            {mission.cycleActif && <Badge libelle="Mission récurrente" couleur={COULEURS.info} />}
            {meta && <span className="compteur-section" style={{ letterSpacing: '0.14em' }}>{meta}</span>}
          </div>
          <h1 style={{ fontSize: '36px', lineHeight: 1.1 }}>{mission.titre}</h1>
          {onglet === 'suivi' && mission.description && (
            <p style={{ fontSize: '15px', color: COULEURS.texteSecondaire, maxWidth: '720px', lineHeight: 1.5 }}>{mission.description}</p>
          )}
        </div>
        {onglet === 'suivi' && (
          <div className="ligne">
            {actionPrincipale}
            <a href={`mailto:missions@tribe.sn?subject=${sujetAjustement}`} className="bouton bouton--secondaire">Demander un ajustement</a>
          </div>
        )}
        {onglet === 'resultats' && (
          <div className="ligne">
            <button type="button" className="bouton" onClick={exporter} disabled={exportEnCours || !collectesChargees}>
              <IconeTelecharger />
              {exportEnCours ? 'Préparation…' : `Exporter en Excel · ${collectes.length.toLocaleString('fr-FR')} lieu${collectes.length > 1 ? 'x' : ''}`}
            </button>
            <BoutonAide texte={AIDE.exportExcel} />
          </div>
        )}
      </header>

      <OngletsLiens prefixe="mission" libelle="Sections de la mission" onglets={onglets} actif={onglet}
        lien={cle => ({ search: cle === 'suivi' ? '' : `?onglet=${cle}` })} />

      {erreurAction && <Message genre="erreur" onFermer={() => setErreurAction('')}>{erreurAction}</Message>}

      {/* ═══ SUIVI ═══ */}
      <div {...panneauOnglet('mission', 'suivi', onglet)} className="panneau-onglet">
        <div className="pile" style={{ gap: '26px' }}>
          {alerteRafraichissement}

          <section aria-label="Chiffres clés" className="grille-4">
            <div className="carte carte--forte" style={{ padding: '20px 22px', gap: '6px' }}>
              <div className="ligne" style={{ gap: '8px' }}>
                <span className="etiquette-mono" style={{ color: '#FF7A3D' }}>Lieux validés</span>
                <BoutonAide texte={AIDE.progression} />
              </div>
              <div className="chiffre">
                {validees.toLocaleString('fr-FR')}{' '}
                <span style={{ fontSize: '18px', color: 'rgba(242,232,212,0.6)' }}>/ {attendues.toLocaleString('fr-FR')}</span>
              </div>
              <div className="progression progression--fine progression--sur-fonce" style={{ marginTop: '6px' }}
                role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pctValidees} aria-label="Lieux validés sur lieux prévus">
                <div className="progression__barre" style={{ width: `${pctValidees}%`, background: '#FF7A3D' }} />
              </div>
              <div style={{ fontSize: '13px', color: 'rgba(242,232,212,0.75)' }}>
                {`+${depuisHier} depuis hier`}{rythme ? ` · rythme ${rythme} par Spotter et par jour` : ''}
              </div>
            </div>

            <div className="carte" style={{ padding: '20px 22px', gap: '6px' }}>
              <span className="etiquette-mono" style={{ color: CHARTE.ocreTexte }}>En vérification</span>
              <div className="chiffre">{enVerification.toLocaleString('fr-FR')}</div>
              <div style={{ fontSize: '13px', color: COULEURS.texteSecondaire, marginTop: 'auto' }}>
                Double validation en cours. Visibles dans les résultats après vérification par Tribe.
              </div>
            </div>

            <div className="carte" style={{ padding: '20px 22px', gap: '6px' }}>
              <span className="etiquette-mono" style={{ color: COULEURS.succes }}>Qualité</span>
              <div className="chiffre">{qualite != null ? `${qualite} %` : '—'}</div>
              <div style={{ fontSize: '13px', color: COULEURS.texteSecondaire, marginTop: 'auto' }}>
                {qualite != null ? 'Collectes validées parmi celles vérifiées par Tribe.' : 'Aucune collecte vérifiée pour le moment.'}
                {fraicheur != null ? ` Fraîcheur moyenne ${fraicheur} jour${fraicheur > 1 ? 's' : ''}.` : ''}
              </div>
            </div>

            <div className="carte" style={{ padding: '20px 22px', gap: '8px' }} role="region" aria-label="Spotters mobilisés">
              <div className="ligne" style={{ gap: '8px' }}>
                <span className="etiquette-mono" style={{ color: COULEURS.info }}>Spotters mobilisés</span>
                <BoutonAide texte={AIDE.spotters} />
              </div>
              <div className="chiffre">{spotters ? spotters.mobilises : '—'}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: 'auto' }} aria-label="Répartition par niveau">
                {!spotters || spotters.mobilises === 0 ? (
                  <span style={{ fontSize: '12px', color: COULEURS.texteSecondaire }}>Aucun Spotter n'a encore collecté.</span>
                ) : spotters.parNiveau.map(n => (
                  <div key={n.niveau} className="ligne ligne--entre" style={{ fontSize: '12px', color: COULEURS.texteSecondaire, flexWrap: 'nowrap' }}>
                    <span>Niveau {n.niveau} · {n.nom}</span>
                    <span className="mono">{n.nombre}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="fiche-suivi">
            <div className="pile" style={{ gap: '12px' }}>
              <div className="ligne ligne--entre" style={{ alignItems: 'baseline' }}>
                <div className="ligne" style={{ gap: '8px' }}>
                  <h2 style={{ fontSize: '20px' }}>Couverture</h2>
                  <BoutonAide texte={AIDE.collectesCarte} />
                </div>
                <ul className="legende" style={{ listStyle: 'none' }} aria-label="Légende de la carte">
                  {Object.entries(COULEUR_COLLECTE).map(([statut, couleur]) => (
                    <li key={statut}><span className="legende__point" style={{ background: couleur }} aria-hidden />{LIBELLE_STATUT_COLLECTE[statut]}</li>
                  ))}
                </ul>
              </div>
              <div className="cadre-carte">
                <div ref={mapRef} style={{ position: 'absolute', inset: 0 }} role="region" aria-label="Carte des collectes" />
                <div className="cadre-carte__legende-fond">FOND CARTO VOYAGER{categorieMission(mission) ? ` · ${categorieMission(mission).toUpperCase()}` : ''}</div>
              </div>
              <div className="ligne ligne--entre" style={{ fontSize: '13px', color: COULEURS.texteSecondaire }}>
                <span>{nb('valide')} validées · {nb('en_attente')} en vérification · {nb('rejete')} écartées</span>
                {majLe && !echecDepuis && <span>Mis à jour à {fmtHeure(majLe)}</span>}
              </div>
            </div>

            <div className="pile" style={{ gap: '12px' }}>
              <h2 style={{ fontSize: '20px' }}>Fil de la mission</h2>
              <div className="bloc">
                {fil.length === 0 ? (
                  <p className="texte-secondaire">Les étapes de la mission s'afficheront ici.</p>
                ) : (
                  <ol className="fil" style={{ listStyle: 'none' }} aria-label="Fil de la mission">
                    {fil.map(e => (
                      <li key={e.cle} className="fil__evenement">
                        <div className="fil__repere" aria-hidden><span className="fil__point" style={{ background: e.couleur }} /><span className="fil__trait" /></div>
                        <div>
                          <div className="fil__quand" style={e.enAttente ? { color: COULEURS.action } : undefined}>{e.quand}</div>
                          <div className="fil__quoi">{e.quoi}</div>
                          {e.detail && <div className="fil__detail">{e.detail}</div>}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
              <div className="carte carte--forte" style={{ padding: '18px 20px', gap: '8px' }}>
                <span className="etiquette-mono" style={{ color: '#E0A848' }}>Votre interlocuteur</span>
                <div style={{ fontWeight: 600, fontSize: '15px' }}>L'équipe missions · Tribe Sénégal</div>
                <div style={{ fontSize: '13px', color: 'rgba(242,232,212,0.75)' }}>
                  <a href="mailto:missions@tribe.sn" style={{ color: 'inherit' }}>missions@tribe.sn</a>
                </div>
              </div>
            </div>
          </section>

          <section className="bloc" aria-labelledby="titre-champs">
            <TitreSection titre="Informations relevées" id="titre-champs" aide={AIDE.champsCollecte} niveau={3} />
            {champs.length > 0 ? (
              <ul className="ligne" style={{ listStyle: 'none', gap: '8px', marginTop: '12px' }}>
                {champs.map((c, i) => (
                  <li key={c.id || i} className="etiquette-type">
                    {libelleChamp(c) || '—'} · {FIELD_TYPE_LABELS[c.type || ''] ?? c.type ?? '—'}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="texte-secondaire" style={{ marginTop: '8px' }}>Les informations relevées seront définies lors de la validation par Tribe.</p>
            )}
          </section>

          {mission.cycleActif && (
            <section aria-labelledby="titre-cycles" className="pile" style={{ gap: '12px' }}>
              <TitreSection titre="Cycles" id="titre-cycles" aide={AIDE.cycles} niveau={3} />
              {cycles.length === 0 ? <p className="texte-secondaire">Aucun cycle créé pour le moment.</p> : (
                <div className="bloc bloc--sans-marge">
                  <table className="tableau">
                    <thead><tr><th scope="col">Cycle</th><th scope="col">Dates</th><th scope="col">Statut</th><th scope="col">Avancement</th></tr></thead>
                    <tbody>
                      {cycles.map((c, i) => {
                        const sc = styleStatutMission(c.statut);
                        return (
                          <tr key={idMission(c) || i}>
                            <td data-label="Cycle">{c.titre || `Cycle ${i + 1}`}</td>
                            <td data-label="Dates">{fmtDate(c.dateDebut)} → {fmtDate(c.dateFin)}</td>
                            <td data-label="Statut"><Badge libelle={sc.libelle} couleur={sc.couleur} /></td>
                            <td data-label="Avancement" className="nombre">{c.collectesRealisees ?? 0} / {c.collectesAttendues ?? 0} ({avancement(c)} %)</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </div>
      </div>

      {/* ═══ RÉSULTATS ═══ */}
      <div {...panneauOnglet('mission', 'resultats', onglet)} className="panneau-onglet">
        {alerteRafraichissement}
        <ResultatsMission collectes={collectes} champs={champs} chargees={collectesChargees} />
      </div>

      {/* ═══ DOCUMENTS ═══ */}
      <div {...panneauOnglet('mission', 'documents', onglet)} className="panneau-onglet">
        <DocumentsMission mission={mission} documents={documents} erreur={erreurDocuments} pdfEnCours={pdfEnCours} onPdf={pdf} />
      </div>
    </div>
  );
};

export default MissionDetail;
